import http from '../http/httpClient'

/**
 * Browser producer for the Source Tracking v1 contract (TRDEV-33311).
 *
 * Builds the Pinpoint-compatible envelope collected by applicant-bff at
 * `POST /source-tracking/events` and delivers it asynchronously. Delivery never throws and never
 * blocks the caller. Only 503 (delivery.failed) and network errors are retried (4xx never are,
 * including 429), and every retry reuses the same `migration_event_id` so it is idempotent
 * downstream. This file is shared verbatim between applicant-ui and
 * social-recruiting-applicant-ui — keep them in sync.
 */

export const CONTRACT_VERSION = '1'
export const EVENT_VERSION = '3.1'
export const SDK = { name: 'source-tracking-web', version: '1' }
export const COLLECTOR_PATH = '/source-tracking/events'

const RETRYABLE_STATUSES = [503]
const DEFAULT_MAX_ATTEMPTS = 3
const DEFAULT_BACKOFF_MILLIS = 500
export const REQUEST_TIMEOUT_MILLIS = 5000

export const REQUEST_HEADERS = Object.freeze({ 'Content-Type': 'application/json' })

/**
 * The default XHR adapter cancels in-flight requests when the page unloads (e.g. the applicant
 * navigates away right after the posting loads). The fetch adapter with `keepalive` lets the
 * browser finish the request; a timeout rejects without a response, so it is retried like any
 * other network error.
 */
export const REQUEST_CONFIG = Object.freeze({
  adapter: 'fetch',
  fetchOptions: Object.freeze({ keepalive: true }),
  timeout: REQUEST_TIMEOUT_MILLIS
})

export const MAX_PAYLOAD_BYTES = 32768
export const MAX_ATTRIBUTES = 60
export const MAX_ATTRIBUTE_VALUE_LENGTH = 1024
const KEY_PATTERN = /^[A-Za-z][A-Za-z0-9_]{0,63}$/
const CONTROL_CHARS = /[\u0000-\u001F\u007F-\u009F]/g // eslint-disable-line no-control-regex

export function newId () {
  const webCrypto = typeof window !== 'undefined' ? window.crypto : undefined
  if (webCrypto && typeof webCrypto.randomUUID === 'function') {
    return webCrypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.floor(Math.random() * 16)
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

/**
 * Pinpoint attributes are flat strings: coerce every value, mirror `undefined`/`null` to '', and
 * keep within the collector limits (key pattern, max 60 entries, max 1024 chars, no control chars).
 */
export function toStringAttributes (attributes = {}) {
  return Object.entries(attributes || {})
    .filter(([key]) => KEY_PATTERN.test(key))
    .slice(0, MAX_ATTRIBUTES)
    .reduce((acc, [key, value]) => {
      const text = value === undefined || value === null ? '' : String(value)
      acc[key] = text.replace(CONTROL_CHARS, '').slice(0, MAX_ATTRIBUTE_VALUE_LENGTH)
      return acc
    }, {})
}

/** Per-application ids, reused while the same posting is being applied to (see Application.js). */
export function applicationTrackingIds (existing, postingId) {
  if (existing && existing.postingId === postingId) {
    return existing
  }
  return { postingId, migrationEventId: newId(), sessionId: newId(), sessionStartTimestamp: Date.now() }
}

export function payloadBytes (envelope) {
  return new window.Blob([JSON.stringify(envelope)]).size
}

function localeParts () {
  const raw = (typeof navigator !== 'undefined' && navigator.language) || 'en-US'
  const [language = 'en', country = ''] = raw.split(/[-_]/)
  return { code: country ? `${language}_${country}` : language, country, language }
}

/**
 * @param producer  { environment, appId, title, packageName, clientId, sessionId, sessionStartTimestamp }
 * @param event     { name, attributes, metrics, sessionId, sessionStartTimestamp, migrationEventId, timestamp }
 */
export function buildEvent (producer, event) {
  const now = event.timestamp || Date.now()
  return {
    source_tracking_contract_version: CONTRACT_VERSION,
    migration_event_id: event.migrationEventId || newId(),
    environment: producer.environment,
    event_type: event.name,
    event_timestamp: now,
    event_version: EVENT_VERSION,
    application: {
      app_id: producer.appId,
      package_name: producer.packageName,
      title: producer.title,
      version_name: '',
      version_code: '1',
      sdk: SDK
    },
    client: { client_id: producer.clientId },
    device: {
      locale: localeParts(),
      make: '',
      model: '',
      platform: { name: 'web', version: '' }
    },
    session: {
      session_id: event.sessionId || producer.sessionId || newId(),
      start_timestamp: event.sessionStartTimestamp || producer.sessionStartTimestamp || now,
      stop_timestamp: null
    },
    attributes: toStringAttributes(event.attributes),
    metrics: event.metrics || {}
  }
}

const sleep = ms => new Promise(resolve => setTimeout(resolve, ms))

function isRetryable (error) {
  const status = error && error.response && error.response.status
  return !status || RETRYABLE_STATUSES.includes(status)
}

/**
 * POST the built envelope to the collector. Resolves to true/false and never rejects.
 * The same serialized event (same migration_event_id) is re-sent on each attempt.
 */
export async function deliver (bffURL, envelope, options = {}) {
  const maxAttempts = options.maxAttempts || DEFAULT_MAX_ATTEMPTS
  const backoff = options.backoffMillis === undefined ? DEFAULT_BACKOFF_MILLIS : options.backoffMillis
  const url = `${bffURL}${COLLECTOR_PATH}`
  if (payloadBytes(envelope) > MAX_PAYLOAD_BYTES) {
    console.error('Source tracking event exceeds collector payload limit', envelope.migration_event_id)
    return false
  }
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      // Unauthenticated POST through the project client (same convention as /profiles/applied).
      await http.post(url, envelope, undefined, REQUEST_HEADERS, REQUEST_CONFIG)
      return true
    } catch (error) {
      if (attempt >= maxAttempts || !isRetryable(error)) {
        console.error('Source tracking delivery failed', envelope.migration_event_id, error && error.message)
        return false
      }
      await sleep(backoff * attempt)
    }
  }
  return false
}

/**
 * Fire-and-forget helper used by producers: builds and delivers on the next tick so the
 * application flow is never blocked or failed by analytics.
 */
export function track (producer, bffURL, event, options) {
  const envelope = buildEvent(producer, event)
  const pending = Promise.resolve().then(() => deliver(bffURL, envelope, options)).catch(() => false)
  return { migrationEventId: envelope.migration_event_id, pending }
}
